import { ApplicationConfig, importProvidersFrom, APP_INITIALIZER, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
} from '@azure/msal-browser';
import {
  MsalInterceptor,
  MSAL_INSTANCE,
  MsalInterceptorConfiguration,
  MsalGuardConfiguration,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
} from '@azure/msal-angular';
import { environment } from '../environments/environment';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatListModule } from '@angular/material/list';
import { AuthInterceptor } from './auth.interceptor';
import { SubmitDiagnosticsService } from './services/diagnostics/submit-diagnostics.service';
import { AccessControlService } from './services/access-control/access-control.service';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { provideNativeDateAdapter } from '@angular/material/core';

function clearLegacyMsalCache(): void {
  // v3 cache keys start with 'msal.' — v4/v5 use a client-ID-prefixed format.
  // Run once per browser on first v5 boot to avoid stale token interference.
  const migrationFlag = 'lpintranet.msal-v5-migrated';
  if (!localStorage.getItem(migrationFlag)) {
    Object.keys(localStorage)
      .filter(k => k.startsWith('msal.'))
      .forEach(k => localStorage.removeItem(k));
    localStorage.setItem(migrationFlag, '1');
  }
}

export function MSALInstanceFactory(): IPublicClientApplication {
  clearLegacyMsalCache();
  return new PublicClientApplication(environment.msalConfig);
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(
    environment.apiConfig.uri,
    environment.apiConfig.scopes
  );

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.apiConfig.scopes],
    },
    loginFailedRoute: '/login-failed',
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),
    importProvidersFrom(
      MatButtonModule,
      MatToolbarModule,
      MatListModule,
      MatMenuModule
    ),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi()),
    provideNativeDateAdapter(),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    // Ensure MSAL Browser is fully initialized before any component mounts.
    // Calling just initialize() isn't sufficient — MSAL Browser v5 may queue
    // acquireTokenSilent() until handleRedirectPromise() has also run.
    // Running the full handleRedirectObservable() sequence here (initialize →
    // handleRedirectPromise → resetInProgressEvent) guarantees MSAL is ready
    // before the first authenticated GET fires (e.g. sse-checklist loading its
    // record in ngOnInit). Both initialize() and handleRedirectPromise() are
    // idempotent — the redundant call from AppComponent is a safe no-op.
    {
      provide: APP_INITIALIZER,
      useFactory: (msalService: MsalService, accessControl: AccessControlService) => async () => {
        try {
          await msalService.handleRedirectObservable().toPromise();
        } catch (e) {
          // Stale or invalid redirect state — log and proceed rather than
          // blocking app startup.
          console.warn('MSAL init: handleRedirectObservable error (non-fatal):', e);
        }
        // Eagerly set the active account so getActiveAccount() is reliable
        // before the first component renders (hub fires its /api/news GET in
        // ngOnInit, which runs before AppComponent's inProgress$ subscription
        // has had a chance to call checkAndSetActiveAccount()).
        try {
          if (!msalService.instance.getActiveAccount()) {
            const accounts = msalService.instance.getAllAccounts();
            if (accounts.length > 0) {
              msalService.instance.setActiveAccount(accounts[0]);
            }
          }
        } catch {
          // Non-fatal — AppComponent.inProgress$ subscription serves as fallback.
        }
        // Load component-access grants so ComponentAccessGuard/SuperAdminGuard can
        // check hasAccess() synchronously thereafter. Fails closed: if this errors
        // (e.g. backend unreachable), AccessControlService keeps an empty grant set.
        try {
          await accessControl.loadPermissions().toPromise();
        } catch (e) {
          console.warn('AccessControlService: loadPermissions error (non-fatal, fails closed):', e);
        }
      },
      deps: [MsalService, AccessControlService],
      multi: true,
    },
    // Register page-lifecycle listeners for submit diagnostics at startup so the
    // visibility/bfcache counters are accurate for the whole page-life.
    {
      provide: APP_INITIALIZER,
      useFactory: (diag: SubmitDiagnosticsService) => () => diag.init(),
      deps: [SubmitDiagnosticsService],
      multi: true,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    provideCharts(withDefaultRegisterables()),
  ],
};
